import { RAW_PAX_CREDITS_PER_DISPLAY_CREDIT } from "@pax/api-contract/accounting/v1alpha1";
import {
  autoTopUpThresholdExceedsPackMessage,
  enterThresholdMessage,
  MAX_AUTO_THRESHOLD_CREDITS,
  type Offer,
  type PackOfferId,
  type SavedPaymentMethod,
} from "@pax/api-contract/payments/v1alpha1";

export type AutoTopUpDraft = {
  autoOfferId: PackOfferId;
  autoThresholdRaw: string;
  /** Sent when the player ticked the cancellation waiver, for every automatic top-up the rule buys. */
  acceptsCancellationWaiver?: true;
};

export type CreditOffer = Extract<Offer, { creditsRaw: string }>;
export type PackOffer = Extract<Offer, { offerId: PackOfferId }>;

const SMALLEST_PRECISE = 0.0001;
const RAW_PER_HUNDREDTH = RAW_PAX_CREDITS_PER_DISPLAY_CREDIT / 100n;
const MAX_THRESHOLD_RAW = MAX_AUTO_THRESHOLD_CREDITS * RAW_PAX_CREDITS_PER_DISPLAY_CREDIT;

export function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "usd" });
}

export function creditsOf(offer: CreditOffer): number {
  return Number(BigInt(offer.creditsRaw)) / Number(RAW_PAX_CREDITS_PER_DISPLAY_CREDIT);
}

export function paymentMethodLabel(method: SavedPaymentMethod): string {
  return method.detail ? `${method.label} ${method.detail}` : method.label;
}

function thresholdRawFromTyped(typed: string): bigint | null {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(typed.trim());
  if (!match) return null;
  const hundredths = BigInt(match[1] ?? "0") * 100n + BigInt((match[2] ?? "").padEnd(2, "0"));
  return hundredths * RAW_PER_HUNDREDTH;
}

/** Inline validation for the threshold field while the player types. */
export function thresholdFieldError(typed: string, pack: PackOffer | null): string | null {
  const trimmed = typed.trim();
  if (!trimmed) return enterThresholdMessage;
  if (!pack) return null;
  const raw = thresholdRawFromTyped(trimmed);
  if (raw === null) {
    return `Use up to two decimals, at most ${creditsOf(pack)} for this pack.`;
  }
  if (raw <= 0n) return enterThresholdMessage;
  if (raw > BigInt(pack.creditsRaw)) return autoTopUpThresholdExceedsPackMessage;
  if (raw > MAX_THRESHOLD_RAW) {
    return `Use up to two decimals, at most ${creditsOf(pack)} for this pack.`;
  }
  return null;
}

/** Raw credits for a valid threshold, or null when {@link thresholdFieldError} would apply. */
export function parseThreshold(typed: string, pack: PackOffer | null): string | null {
  if (thresholdFieldError(typed, pack)) return null;
  const raw = thresholdRawFromTyped(typed.trim());
  return raw?.toString() ?? null;
}

/** A threshold's raw credits as display credits, rounded to the hundredth it was set in. */
export function thresholdCredits(raw: string): string {
  const hundredths = BigInt(raw) / RAW_PER_HUNDREDTH;
  const whole = hundredths / 100n;
  const cents = (hundredths % 100n).toString().padStart(2, "0").replace(/0+$/, "");
  return cents ? `${whole}.${cents}` : whole.toString();
}

const RAW_PER_TEN_THOUSANDTH = RAW_PAX_CREDITS_PER_DISPLAY_CREDIT / 10_000n;

const decimalSeparator =
  new Intl.NumberFormat().formatToParts(1.5).find(part => part.type === "decimal")?.value ?? ".";

/** Raw credits as display credits, for placing on a chart. Exact enough to draw, not to print. */
export function creditsFromRaw(raw: string): number {
  const amount = BigInt(raw);
  return (
    Number(amount / RAW_PAX_CREDITS_PER_DISPLAY_CREDIT) +
    Number(amount % RAW_PAX_CREDITS_PER_DISPLAY_CREDIT) / Number(RAW_PAX_CREDITS_PER_DISPLAY_CREDIT)
  );
}

/**
 * Raw credits of any size as display credits, worked out without floating point: four decimals,
 * and a nonzero amount as at least 0.0001.
 */
export function formatRawCredits(raw: string, { signed = false } = {}): string {
  const amount = BigInt(raw);
  const magnitude = amount < 0n ? -amount : amount;
  const rounded = (magnitude + RAW_PER_TEN_THOUSANDTH / 2n) / RAW_PER_TEN_THOUSANDTH;
  const tenThousandths = rounded === 0n && magnitude > 0n ? 1n : rounded;
  const whole = (tenThousandths / 10_000n).toLocaleString();
  const fraction = (tenThousandths % 10_000n).toString().padStart(4, "0");
  const sign = amount < 0n ? "-" : signed && amount > 0n ? "+" : "";
  return `${sign}${whole}${decimalSeparator}${fraction}`;
}

/** Typed display credits as raw credits, or null unless it is a positive amount with up to four decimals. */
export function rawFromTypedCredits(typed: string): bigint | null {
  const match = /^(\d+)(?:\.(\d{1,4}))?$/.exec(typed.trim());
  if (!match) return null;
  const tenThousandths =
    BigInt(match[1] ?? "0") * 10_000n + BigInt((match[2] ?? "").padEnd(4, "0"));
  return tenThousandths > 0n ? tenThousandths * RAW_PER_TEN_THOUSANDTH : null;
}

/**
 * Two decimals, or four for a charge too small to show in two. `precise` allows four for any
 * amount and shows a nonzero amount as at least 0.0001.
 */
export function formatCredits(credits: number, { signed = false, precise = false } = {}): string {
  const shown =
    precise && credits !== 0 && Math.abs(credits) < SMALLEST_PRECISE
      ? Math.sign(credits) * SMALLEST_PRECISE
      : credits;
  const tiny = shown !== 0 && Math.abs(credits) < 0.01;
  return shown.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: precise || tiny ? 4 : 2,
    signDisplay: signed ? "exceptZero" : "auto",
  });
}
